import { createRoot } from "react-dom/client";
import { createStore } from "redux";
import { Provider } from "react-redux";
import {
    persistStore,
    persistReducer,
    createTransform,
    PersistConfig,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { reducer, State } from "./state";
import { h } from "./h";
import { App } from "./components/App";
import { PersistGate } from "redux-persist/integration/react";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { Action } from "./actions";
import { DataState } from "./state/data";
import { getTodayDate } from "./utils/date";
import { ItemsStateWithHistory } from "./state/days";
import createCompressor from "redux-persist-transform-compress";
import { initApi } from "./services/api";
import { WatchAuthorization } from "./components/WatchAuthorization";

const persistConfig: PersistConfig<State> = {
    key: "root",
    storage,
    stateReconciler: autoMergeLevel2,
    blacklist: [`ui`],
    throttle: 500,
    transforms: [
        createTransform(
            (inboundState: DataState): DataState => {
                const today = getTodayDate();
                const { days } = inboundState;
                const newDays = Object.keys(days).reduce((result, date) => {
                    if (date === today) {
                        result[date] = days[date];
                    } else {
                        result[date] = {
                            past: [],
                            present: days[date].present,
                            future: [],
                        };
                    }
                    return result;
                }, {} as ItemsStateWithHistory);
                return {
                    ...inboundState,
                    days: newDays,
                };
            },
            (outboundState) => outboundState,
            { whitelist: [`data`] },
        ),
        createCompressor(),
    ],
};

const persistedReducer = persistReducer<State, Action>(persistConfig, reducer);

const store = createStore(
    persistedReducer,
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ &&
        (window as any).__REDUX_DEVTOOLS_EXTENSION__(),
);
const persistor = persistStore(store);
initApi(store, persistor);

const domContainer = document.getElementById("app");
if (!domContainer) {
    throw new Error("App container was not found");
}
createRoot(domContainer).render(
    h(
        Provider,
        { store },
        h(
            PersistGate,
            {
                loading: null,
                persistor,
            },
            window.location.pathname === `/watch`
                ? h(WatchAuthorization)
                : h(App),
        ),
    ),
);
